import { useEffect, useMemo, useState } from "react"
import { useArrivalHighlight } from "@/shared/hooks/useArrivalHighlight"
import socialMediaData from "src/shared/data/socialMediaData"
import type { BudgetTreeLaunch, BudgetTreeNetwork } from "@models/engagementBudgetTree.model"

/*
    These are money/percent fields, so keystrokes are filtered to digits, a single decimal
    point, and thousands commas. Sanitizing in onChange (rather than blocking keydown) also
    covers paste and drag-drop, which keydown handlers miss.
*/
export const numericOnly = (raw: string) => {
  const cleaned = raw.replace(/[^\d.,]/g, "")
  const firstDot = cleaned.indexOf(".")
  if (firstDot === -1) return cleaned
  // Keep the first decimal point; drop any later ones.
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "")
}

const round2 = (n: number) => Math.round(n * 100) / 100
const roundPct = (n: number) => Math.round(n * 1e6) / 1e6
const fmt = (v: number) => v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const effectiveDays = (nodeDays: number, windowDays: number) =>
  windowDays > 0 ? Math.min(nodeDays, windowDays) : nodeDays
const capDaily = (daily: number, cap: number | null) => (cap != null ? Math.min(daily, cap) : daily)

function networkDisplay(key: string) {
  const k = key.toLowerCase()
  const d = socialMediaData[k]
  return { name: d?.label ?? key, color: d?.color ?? "#667eea" }
}

// Networks whose bid sits on the AD GROUP (ad set / ad squad / ad group) — editable on
// the audience rows.
const AD_GROUP_BID_NETWORKS = new Set(["meta", "snapchat", "pinterest", "linkedin"])
// Networks whose bid sits on the CAMPAIGN — editable on the network row instead. Google
// Demand Gen bidding is a campaign-level setting, so an ad-group bid has nowhere to go.
const CAMPAIGN_BID_NETWORKS = new Set(["google", "vibe"])
// The campaign-level bid is a max cost per CLICK, not the max CPM it is on the ad-group
// networks (Demand Gen has no impression bid) — same column, different unit, so say so.
const CAMPAIGN_BID_TITLE: Record<string, string> = {
  // Called a maximum here until MPN-1522, which it never was: Demand Gen has no per click
  // ceiling of any kind, and saying otherwise is what sent Ops looking for a bid cap Google
  // does not offer. Some strategies ignore the number entirely, which the publish job says.
  google:
    "Target cost per click, set on the campaign. This is a target average rather than a cap, and Demand Gen offers no maximum CPC. Some bid strategies, Maximize Clicks among them, take no bid at all, in which case the daily budget is the only spend control. Leave blank to use the default.",
  // Vibe's is a target, not a ceiling: spend is bounded by the strategy's daily budget whatever
  // this says, so it makes delivery more or less aggressive rather than more or less expensive.
  vibe:
    "Target cost per session, set on the campaign. This is a target rather than a cap — daily budget is what bounds spend. Leave blank to use the imported template's target.",
}

/*
    Strategies that carry no bid amount hide the Bid input entirely (MPN-1584).

    Which strategies those are is decided server-side and arrives as `accepts_bid` -- the
    same BID_SCHEMES table the publish job reads, so the page cannot offer a bid that
    publish would throw away. MPN-1522 established the underlying fact: Demand Gen honours
    no bid cap on Maximize Clicks, and an amount entered against it goes nowhere.

    The Maximize Clicks wording is quoted from the ticket. Four other Demand Gen strategies
    carry no amount either and would discard a bid identically, so they get the same
    sentence with their own name rather than a claim about Maximize Clicks.
*/
const NO_BID_MESSAGE: Record<string, string> = {
  MAXIMIZE_CLICKS:
    "Google does not support a bid cap for Maximize Clicks on Demand Gen — Daily Budget is your only spend control.",
}

const STRATEGY_LABEL: Record<string, string> = {
  TARGET_CPC: "Target CPC",
  TARGET_CPA: "Target CPA",
  TARGET_SPEND: "Maximize Clicks",
  MAXIMIZE_CLICKS: "Maximize Clicks",
  MAXIMIZE_CONVERSIONS: "Maximize Conversions",
  MAXIMIZE_CONVERSION_VALUE: "Maximize Conversion Value",
  TARGET_ROAS: "Target ROAS",
}

function strategyLabel(strategy?: string | null): string | null {
  if (!strategy) return null
  return STRATEGY_LABEL[strategy] ?? strategy
}

function noBidMessage(strategy?: string | null): string {
  const named = strategy ? NO_BID_MESSAGE[strategy] : undefined
  if (named) return named
  const label = strategyLabel(strategy) ?? "this bid strategy"
  return `Google does not support a bid cap for ${label} on Demand Gen — Daily Budget is your only spend control.`
}

// What unit the Bid column is denominated in, per network. Static: it follows from how each
// network's publish command bids (impression-capped ad groups vs Google's maximize-clicks
// CPC ceiling), not from anything the user picks. Blank for networks we don't bid on.
const BID_UNIT: Record<string, string> = {
  meta: "CPM",
  pinterest: "CPM",
  snapchat: "CPM",
  google: "CPC",
  linkedin: "CPM",
  // Cost per session: connected TV has no click to bid on.
  vibe: "CPS",
}

type DraftMap = Record<string, number>

export function LaunchNetworksTable({
  launch,
  spendWindowDays,
  editing,
  onSetNetwork,
  onSetNetworkCap,
  onSetAudience,
}: {
  launch: BudgetTreeLaunch
  spendWindowDays: number
  editing: boolean
  onSetNetwork: (network: string, percent: number, dailyCap: number | null, bid: number | null) => void
  onSetNetworkCap: (
    network: string,
    networkPercent: number,
    dailyCap: number | null,
    bid: number | null,
    adGroups: { adGroupId: number; percent: number; dailyCap: number | null; bid: number | null }[],
  ) => void
  onSetAudience: (
    adGroupId: number,
    network: string,
    percent: number,
    dailyCap: number | null,
    bid: number | null,
  ) => void
}) {
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(launch.networks.map((n) => n.network)),
  )
  const toggle = (k: string) =>
    setExpanded((p) => {
      const n = new Set(p)
      n.has(k) ? n.delete(k) : n.add(k)
      return n
    })

  const [networkDrafts, setNetworkDrafts] = useState<DraftMap>({})
  // Opened from a failed publish: flash the network row the error was about.
  const highlight = useArrivalHighlight(".budget-table__row--highlight")
  const matchesHighlight = (token: string) =>
    highlight?.toLowerCase() === token.toLowerCase()
  const [audienceDrafts, setAudienceDrafts] = useState<Record<number, number>>({})

  useEffect(() => {
    setNetworkDrafts({})
    setAudienceDrafts({})
    setExpanded(new Set(launch.networks.map((n) => n.network)))
  }, [launch.launch_id, launch.networks])

  const livePctFor = (network: string, fallback: number) =>
    networkDrafts[network] ?? fallback
  const liveAudPctFor = (audId: number, fallback: number) =>
    audienceDrafts[audId] ?? fallback

  // Paused networks are listed but spend nothing, so they stay out of every total
  // (MPN-1486). Counting them would show a campaign as fully allocated while part of it
  // is switched off, which is the misreading this ticket is about.
  const serving = launch.networks.filter((n) => !n.is_paused && n.is_configured !== false)

  // Inactive networks sink to the bottom (MPN-1534). "Inactive" is one flag, not two:
  // is_paused is is_enabled=false, which is equally true of a network switched off after
  // running and one that was never switched on, and the ticket asks for both to be
  // treated the same. Sorting is stable, so the order Ops already knows survives within
  // each group -- this moves rows down, it does not reshuffle them.
  const orderedNetworks = useMemo(
    () =>
      launch.networks
        // Networks the client has no resource for are dropped, not greyed (MPN-1624).
        // Greying says "inactive, switch it on from here"; there is no account behind
        // these to switch on, so the row is noise that grows with every network added.
        // `!== false` rather than truthiness: a server without the field shows
        // everything, which is the old behaviour rather than an empty table.
        .filter((n) => n.is_configured !== false)
        .slice()
        .sort((a, b) => Number(a.is_paused) - Number(b.is_paused)),
    [launch.networks],
  )
  const liveTotalPct = serving.reduce(
    (s, n) => s + livePctFor(n.network, n.percent),
    0,
  )
  const liveTotalResolved = serving.reduce(
    (s, n) => s + (launch.resolved_amount * livePctFor(n.network, n.percent)) / 100,
    0,
  )
  const liveTotalDaily = serving.reduce((s, n) => {
    const days = effectiveDays(n.active_days, spendWindowDays)
    if (days <= 0) return s
    const pct = livePctFor(n.network, n.percent)
    const resolved = (launch.resolved_amount * pct) / 100
    return s + capDaily(resolved / days, n.daily_spend_cap)
  }, 0)
  const overTotal = liveTotalPct > 100 + 0.01
  const unallocated = round2(100 - liveTotalPct)
  const unallocatedAmount = round2(launch.resolved_amount * (unallocated / 100))

  return (
    <table className="budget-table budget-table--networks">
      <thead className="budget-table__head">
        <tr>
          <th className="col-name">Network / Ad Group</th>
          <th className="col-pct">
            Percent <span className="budget-col-unit">(%)</span>
          </th>
          <th className="col-resolved">
            Amount <span className="budget-col-unit">($)</span>
          </th>
          <th className="col-daily col-daily--primary">
            Daily <span className="budget-col-unit">($)</span>
          </th>
          <th className="col-cap">
            Daily Cap <span className="budget-col-unit">($)</span>
          </th>
          <th className="col-bidstrategy">Bid Strategy</th>
          <th className="col-bid">
            Bid <span className="budget-col-unit">($)</span>
          </th>
          <th className="col-alloc" />
        </tr>
      </thead>
      <tbody>
        {orderedNetworks.map((n) => {
          const sibling = serving.reduce(
            (s, x) => s + (x.network === n.network ? 0 : livePctFor(x.network, x.percent)),
            0,
          )
          const display = networkDisplay(n.network)
          const isExpanded = expanded.has(n.network)
          const highlighted = matchesHighlight(`network:${n.network}`)
          return (
            <NetworkRows
              key={n.network}
              highlighted={highlighted}
              matchesHighlight={matchesHighlight}
              network={n}
              parentResolved={launch.resolved_amount}
              spendWindowDays={spendWindowDays}
              display={display}
              expanded={isExpanded}
              onToggle={() => toggle(n.network)}
              editing={editing}
              siblingSumExcludingSelf={sibling}
              draftPercent={networkDrafts[n.network]}
              onDraftChange={(p) =>
                setNetworkDrafts((prev) => ({ ...prev, [n.network]: p }))
              }
              onCommitPercent={(pct) => {
                onSetNetwork(n.network, pct, n.daily_spend_cap, n.bid)
                setNetworkDrafts((prev) => {
                  const { [n.network]: _, ...rest } = prev
                  return rest
                })
              }}
              onCommitCap={(cap) =>
                onSetNetworkCap(
                  n.network,
                  n.percent,
                  cap,
                  n.bid,
                  n.ad_groups.map((a) => ({
                    adGroupId: a.ad_group_id,
                    percent: a.percent,
                    // Split the network cap across audiences by budget share.
                    dailyCap:
                      cap == null || n.resolved_amount <= 0
                        ? null
                        : round2(cap * (a.resolved_amount / n.resolved_amount)),
                    bid: a.bid,
                  })),
                )
              }
              onResetDraft={() =>
                setNetworkDrafts((prev) => {
                  const { [n.network]: _, ...rest } = prev
                  return rest
                })
              }
              audienceDrafts={audienceDrafts}
              setAudienceDraft={(audId, p) =>
                setAudienceDrafts((prev) => ({ ...prev, [audId]: p }))
              }
              clearAudienceDraft={(audId) =>
                setAudienceDrafts((prev) => {
                  const { [audId]: _, ...rest } = prev
                  return rest
                })
              }
              liveAudPctFor={liveAudPctFor}
              onCommitAudiencePercent={(audId, pct, cap, bid) => {
                onSetAudience(audId, n.network, pct, cap, bid)
                setAudienceDrafts((prev) => {
                  const { [audId]: _, ...rest } = prev
                  return rest
                })
              }}
              onCommitAudiencePolicy={(audId, pct, cap, bid) => onSetAudience(audId, n.network, pct, cap, bid)}
              onCommitBid={(bid) => onSetNetwork(n.network, n.percent, n.daily_spend_cap, bid)}
            />
          )
        })}
      </tbody>
      <tfoot>
        <tr className="budget-table__total-row">
          <td className="col-name" style={{ fontWeight: 600 }}>Total</td>
          <td className="col-pct">
            <ReadonlyShell suffix="%" valueText={liveTotalPct.toFixed(2)} bold over={overTotal} />
          </td>
          <td className="col-resolved">
            <div className="budget-daily-cell">
              <ReadonlyShell valueText={fmt(liveTotalResolved)} bold />
            </div>
          </td>
          <td className="col-daily col-daily--primary">
            <ReadonlyShell valueText={fmt(liveTotalDaily)} bold />
          </td>
          <td className="col-cap" />
          <td className="col-bidstrategy" />
          <td className="col-bid" />
          <td className="col-alloc" />
        </tr>
        {Math.abs(unallocated) > 0.01 && (
          <tr className="budget-table__unallocated-row">
            <td className="col-name">
              <span className="budget-unallocated">
                {unallocated > 0 ? "Unallocated" : "Over-allocated"}
              </span>
            </td>
            <td className="col-pct">
              <ReadonlyShell suffix="%" valueText={Math.abs(unallocated).toFixed(2)} muted />
            </td>
            <td className="col-resolved">
              <ReadonlyShell valueText={fmt(Math.abs(unallocatedAmount))} muted />
            </td>
            <td className="col-daily col-daily--primary" />
            <td className="col-cap" />
            <td className="col-bidstrategy" />
            <td className="col-bid" />
            <td className="col-alloc" />
          </tr>
        )}
      </tfoot>
    </table>
  )
}

/*
    Why a campaign row can read back a percent nobody typed.

    Once a campaign is split into time segments in Media Spend, this row is the sum of that
    network across every segment, and the server resolves each segment independently: a
    segment with its own hand-set percent keeps it, every other segment follows the number
    entered here. So typing 60 into a campaign whose middle segment is edited to 50 leaves
    the row reading 56 -- correct, and baffling without this pill.

    An edited segment is deliberate work, so an edit here does not overwrite it. Resetting is
    done on the segment, in Media Spend, where the window it belongs to is visible.
*/
const segmentedHint = (segments: number, pinned: number): string =>
  pinned > 0
    ? `Split across ${segments} time segments, ${pinned} with a percent set by hand in Media Spend. ` +
      `Editing here moves the other ${segments - pinned}; the edited ${pinned === 1 ? "one keeps its" : "ones keep their"} own share, ` +
      `so this row can read back a different number from the one you type. Reset it in Media Spend.`
    : `Split across ${segments} time segments, all following the campaign percent. Editing here moves all of them.`

function NetworkRows({
  highlighted,
  matchesHighlight,
  network,
  parentResolved,
  spendWindowDays,
  display,
  expanded,
  onToggle,
  editing,
  siblingSumExcludingSelf,
  draftPercent,
  onDraftChange,
  onCommitPercent,
  onCommitCap,
  onResetDraft,
  audienceDrafts,
  setAudienceDraft,
  clearAudienceDraft,
  liveAudPctFor,
  onCommitAudiencePercent,
  onCommitAudiencePolicy,
  onCommitBid,
}: {
  highlighted?: boolean
  matchesHighlight?: (token: string) => boolean
  network: BudgetTreeNetwork
  parentResolved: number
  spendWindowDays: number
  display: { name: string; color: string }
  expanded: boolean
  onToggle: () => void
  editing: boolean
  siblingSumExcludingSelf: number
  draftPercent: number | undefined
  onDraftChange: (p: number) => void
  onCommitPercent: (pct: number) => void
  onCommitCap: (cap: number | null) => void
  onResetDraft: () => void
  audienceDrafts: Record<number, number>
  setAudienceDraft: (audId: number, p: number) => void
  clearAudienceDraft: (audId: number) => void
  liveAudPctFor: (audId: number, fallback: number) => number
  onCommitAudiencePercent: (audId: number, pct: number, cap: number | null, bid: number | null) => void
  // Full ad-group policy upsert — used for both cap and bid commits.
  onCommitAudiencePolicy: (audId: number, pct: number, cap: number | null, bid: number | null) => void
  // Network-row bid commit — only wired for campaign-level-bid networks.
  onCommitBid: (bid: number | null) => void
}) {
  const isAuto = !network.has_explicit_policy
  const hasAudiences = network.ad_groups.length > 0
  // Where this network's bid lives decides which row's Bid cell is editable; the other
  // stays read-only. Networks in neither set have no bid support at all.
  const key = network.network.toLowerCase()
  const adGroupBidEditable = AD_GROUP_BID_NETWORKS.has(key)
  const campaignBidEditable = CAMPAIGN_BID_NETWORKS.has(key)
  const bidUnit = BID_UNIT[key]
  const bidUnitTitle = bidUnit
    ? bidUnit === "CPC"
      ? "Bid is a target average cost per click (Demand Gen has no impression bid and no maximum CPC)."
      : "Bid is a maximum cost per 1,000 impressions."
    : "This network has no bid setting."

  return (
    <>
      <CoupledRow
        nameCell={
          <div className="budget-network-cell">
            {hasAudiences ? (
              <button type="button" className="budget-expand-btn" onClick={onToggle}>
                <i className={`bi bi-chevron-${expanded ? "down" : "right"}`} />
              </button>
            ) : (
              <span className="budget-expand-btn budget-expand-btn--empty" />
            )}
            <span className="budget-network-cell__dot" style={{ backgroundColor: display.color }} />
            <span className="budget-network-cell__name">{display.name}</span>
            {isAuto && !network.is_paused && <span className="budget-auto-tag">auto</span>}
            {network.segment_count > 1 && (
              <span
                className="budget-auto-tag budget-auto-tag--segmented"
                title={segmentedHint(network.segment_count, network.pinned_segments)}
              >
                {network.pinned_segments > 0
                  ? `${network.pinned_segments} of ${network.segment_count} pinned`
                  : `${network.segment_count} segments`}
              </span>
            )}
            {network.is_paused && (
              <span
                className="budget-auto-tag budget-auto-tag--paused"
                title="Switched off for this campaign. It is not serving and not spending; the percent shown is the share it gets back when it is switched on."
              >
                paused
              </span>
            )}
          </div>
        }
        serverPercent={network.percent}
        draftPercent={draftPercent}
        parentResolved={parentResolved}
        activeDays={network.active_days}
        spendWindowDays={spendWindowDays}
        dailyCap={network.daily_spend_cap}
        bid={network.bid}
        editing={editing && !network.is_paused}
        isAuto={isAuto}
        siblingSumExcludingSelf={siblingSumExcludingSelf}
        onDraftChange={onDraftChange}
        onCommitPercent={onCommitPercent}
        onCommitCap={onCommitCap}
        onCommitBid={campaignBidEditable ? onCommitBid : undefined}
        bidTitle={campaignBidEditable ? CAMPAIGN_BID_TITLE[key] : undefined}
        bidUnit={bidUnit}
        bidUnitTitle={bidUnitTitle}
        bidStrategy={network.bid_strategy}
        bidStrategyTitle={
          network.accepts_bid === false
            ? noBidMessage(network.bid_strategy)
            : "The bidding strategy this campaign publishes with, from its imported Google template."
        }
        acceptsBid={network.accepts_bid}
        onResetDraft={onResetDraft}
        rowClassName={`${isAuto ? "budget-table__row--auto" : ""}${
          network.is_paused ? " budget-table__row--inactive" : ""
        }${highlighted ? " budget-table__row--highlight" : ""}`}
      />
      {expanded &&
        network.ad_groups.map((a) => {
          const aSibling = network.ad_groups.reduce(
            (s, x) =>
              s + (x.ad_group_id === a.ad_group_id ? 0 : liveAudPctFor(x.ad_group_id, x.percent)),
            0,
          )
          const isAudAuto = !a.has_explicit_policy
          return (
            <CoupledRow
              key={a.ad_group_id}
              nameCell={
                <span className="budget-audience-cell">
                  <span className="budget-audience-cell__name" title={a.ad_group_name}>
                    {a.ad_group_name}
                  </span>
                  {isAudAuto && <span className="budget-auto-tag">auto</span>}
                </span>
              }
              serverPercent={a.percent}
              draftPercent={audienceDrafts[a.ad_group_id]}
              parentResolved={network.resolved_amount}
              activeDays={a.active_days}
              spendWindowDays={spendWindowDays}
              dailyCap={a.daily_spend_cap}
              bid={a.bid}
              // Locked along with its network: editing an audience under a switched-off
              // network would be setting a share of nothing (MPN-1486).
              editing={editing && !network.is_paused}
              isAuto={isAudAuto}
              siblingSumExcludingSelf={aSibling}
              onDraftChange={(p) => setAudienceDraft(a.ad_group_id, p)}
              onCommitPercent={(p) => onCommitAudiencePercent(a.ad_group_id, p, a.daily_spend_cap, a.bid)}
              onCommitCap={(cap) => onCommitAudiencePolicy(a.ad_group_id, a.percent, cap, a.bid)}
              onCommitBid={
                adGroupBidEditable
                  ? (bid) => onCommitAudiencePolicy(a.ad_group_id, a.percent, a.daily_spend_cap, bid)
                  : undefined
              }
              // Shown on every ad-group row, including Google's — where the bid itself is
              // read-only because Demand Gen bids on the campaign. The unit is a fact about
              // the network, so the column reads consistently down the whole table.
              bidUnit={bidUnit}
              bidUnitTitle={bidUnitTitle}
              onResetDraft={() => clearAudienceDraft(a.ad_group_id)}
              rowClassName={`budget-table__row--audience${
                isAudAuto ? " budget-table__row--auto" : ""
              }${network.is_paused ? " budget-table__row--inactive" : ""}${
                matchesHighlight?.(`ad_group:${a.ad_group_id}`)
                  ? " budget-table__row--highlight"
                  : ""
              }`}
            />
          )
        })}
    </>
  )
}

function CoupledRow({
  nameCell,
  serverPercent,
  draftPercent,
  parentResolved,
  activeDays,
  spendWindowDays,
  dailyCap,
  bid,
  editing,
  isAuto,
  siblingSumExcludingSelf,
  onDraftChange,
  onCommitPercent,
  onCommitCap,
  onCommitBid,
  bidTitle,
  bidUnit,
  bidUnitTitle,
  bidStrategy,
  bidStrategyTitle,
  acceptsBid,
  onResetDraft,
  rowClassName,
}: {
  nameCell: React.ReactNode
  serverPercent: number
  draftPercent: number | undefined
  parentResolved: number
  activeDays: number
  spendWindowDays: number
  dailyCap: number | null
  bid: number | null
  editing: boolean
  isAuto: boolean
  siblingSumExcludingSelf: number
  onDraftChange: (p: number) => void
  onCommitPercent: (p: number) => void
  onCommitCap: (cap: number | null) => void
  // Absent when bids can't be set on this row — the cell renders read-only.
  onCommitBid?: (bid: number | null) => void
  // Overrides the Bid cell tooltip where the number doesn't mean "max CPM".
  bidTitle?: string
  // Static unit label for the Bid Strategy cell ("CPM"/"CPC"); blank on rows that don't
  // carry the network identity (audience rows, totals).
  bidUnit?: string
  bidUnitTitle?: string
  bidStrategy?: string | null
  bidStrategyTitle?: string
  /** MPN-1584. Undefined means "not applicable" and behaves as true. */
  acceptsBid?: boolean
  onResetDraft: () => void
  rowClassName?: string
}) {
  const livePct = draftPercent ?? serverPercent
  const liveResolved = (parentResolved * livePct) / 100
  const dailyDays = effectiveDays(activeDays, spendWindowDays)
  const uncappedDaily = dailyDays > 0 ? liveResolved / dailyDays : 0
  const liveDaily = capDaily(uncappedDaily, dailyCap)
  const capBinding = dailyCap != null && uncappedDaily > dailyCap + 0.005

  const projected = livePct + siblingSumExcludingSelf
  const overTotal = projected > 100 + 0.01
  const outOfRange = livePct < 0 || livePct > 100
  const error = (overTotal || outOfRange) && editing
  const isCapped = spendWindowDays > 0 && activeDays > spendWindowDays
  const cappedNote = isCapped
    ? `Capped by spend window. ${activeDays} active days, but only ${spendWindowDays} days of budget remain.`
    : `Amount divided across ${dailyDays} active day${dailyDays === 1 ? "" : "s"}.`

  const commit = () => {
    if (draftPercent === undefined) return
    if (outOfRange) {
      onResetDraft()
      return
    }
    if (roundPct(livePct) !== roundPct(serverPercent)) onCommitPercent(roundPct(livePct))
    else onResetDraft()
  }

  return (
    <tr className={`budget-table__row ${rowClassName ?? ""}${error ? " budget-table__row--warning" : ""}`}>
      <td className="col-name">{nameCell}</td>
      <td className="col-pct">
        <SyncedInput
          value={livePct}
          formattedDisplay={livePct.toFixed(2)}
          editing={editing}
          isAuto={isAuto}
          error={error}
          onChangeNum={(v) => onDraftChange(v)}
          onCommit={commit}
          onCancel={onResetDraft}
        />
      </td>
      <td className="col-resolved" title={cappedNote}>
        <div className="budget-daily-cell">
          <SyncedInput
            value={liveResolved}
            formattedDisplay={fmt(liveResolved)}
            editing={editing}
            isAuto={isAuto}
            error={error}
            onChangeNum={(v) => {
              if (parentResolved > 0) onDraftChange((v / parentResolved) * 100)
            }}
            onCommit={commit}
            onCancel={onResetDraft}
          />
          <span
            className={`budget-daily-divisor${isCapped ? " budget-daily-divisor--capped" : ""}`}
          >
            ÷ {isCapped ? `${dailyDays} of ${activeDays}` : dailyDays} day{dailyDays === 1 ? "" : "s"}
            {isCapped && (
              <i
                className="bi bi-info-circle-fill ms-1"
                title={cappedNote}
                style={{ fontSize: "0.625rem" }}
              />
            )}
          </span>
        </div>
      </td>
      <td
        className="col-daily col-daily--primary"
        title={capBinding ? `Capped at the $${dailyCap?.toFixed(2)} daily spend cap.` : undefined}
      >
        <SyncedInput
          value={uncappedDaily}
          formattedDisplay={fmt(liveDaily)}
          editing={editing}
          isAuto={isAuto}
          error={error}
          bold
          capped={capBinding}
          onChangeNum={(v) => {
            if (parentResolved > 0 && dailyDays > 0) {
              onDraftChange(((v * dailyDays) / parentResolved) * 100)
            }
          }}
          onCommit={commit}
          onCancel={onResetDraft}
        />
      </td>
      <td className="col-cap" title="Fixed daily spend ceiling. The daily budget never exceeds this.">
        <CapInput
          value={dailyCap}
          editing={editing}
          onCommit={onCommitCap}
          title="Fixed daily spend ceiling — leave blank for no cap"
        />
      </td>
      {/*
          The strategy when we know it, the unit otherwise (MPN-1584). This column has been
          labelled "Bid Strategy" while rendering the bid UNIT, which is most of why the
          strategy read like something selectable here. Google now shows the real one.
      */}
      <td className="col-bidstrategy" title={bidStrategy ? bidStrategyTitle : bidUnitTitle}>
        {strategyLabel(bidStrategy) ?? bidUnit ?? ""}
      </td>
      {acceptsBid === false ? (
        /*
            No input at all, not a disabled one. A greyed box still reads as a control
            somebody could enable, and the whole complaint in MPN-1522 was that the field
            implied a control that structurally cannot exist.
        */
        <td className="col-bid col-bid--unsupported" title={noBidMessage(bidStrategy)}>
          <span className="budget-bid-unsupported">
            <i className="bi bi-info-circle" aria-hidden="true" />
            <span className="budget-bid-unsupported__text">Not supported</span>
          </span>
        </td>
      ) : (
        <td className="col-bid" title={bidTitle ?? "Bid passed to the network."}>
          <CapInput
            value={bid}
            editing={editing && onCommitBid != null}
            onCommit={(b) => onCommitBid?.(b)}
            title={bidTitle ?? "Bid passed to the network — leave blank for none"}
          />
        </td>
      )}
      <td className="col-alloc" />
    </tr>
  )
}

function CapInput({
  value,
  editing,
  onCommit,
  title,
}: {
  value: number | null
  editing: boolean
  onCommit: (cap: number | null) => void
  title: string
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const display = draft !== null ? draft : value != null ? value.toFixed(2) : ""
  // Unset and not editing: render an em dash instead of a lone "$" with nothing after it,
  // which read as a broken cell.
  const isEmpty = !editing && value == null
  const cls =
    "budget-inline-input" +
    (!editing ? " budget-inline-input--readonly" : "") +
    (value == null && !editing ? " budget-inline-input--auto" : "")

  if (isEmpty) {
    return (
      <div className="budget-inline-input budget-inline-input--readonly" title={title}>
        <span className="budget-inline-input__empty">—</span>
      </div>
    )
  }

  const commit = () => {
    if (draft === null) return
    const trimmed = draft.trim()
    setDraft(null)
    if (trimmed === "") {
      if (value != null) onCommit(null)
      return
    }
    const next = parseFloat(trimmed.replace(/,/g, ""))
    if (isNaN(next) || next < 0) return
    if (round2(next) !== round2(value ?? NaN)) onCommit(round2(next))
  }

  return (
    <div className={cls} title={title}>
      <input
        type="text"
        inputMode="decimal"
        className="budget-inline-input__field"
        readOnly={!editing}
        tabIndex={editing ? 0 : -1}
        placeholder={editing ? "—" : ""}
        value={display}
        onChange={(e) => setDraft(numericOnly(e.target.value))}
        onFocus={() => editing && setDraft(value != null ? value.toFixed(2) : "")}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setDraft(null)
            ;(e.target as HTMLInputElement).blur()
          } else if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur()
          }
        }}
        onBlur={commit}
      />
    </div>
  )
}

function SyncedInput({
  value,
  formattedDisplay,
  editing,
  isAuto,
  error,
  prefix,
  suffix,
  bold = false,
  capped = false,
  onChangeNum,
  onCommit,
  onCancel,
  extraButton,
}: {
  value: number
  formattedDisplay: string
  editing: boolean
  isAuto: boolean
  error: boolean
  prefix?: string
  suffix?: string
  bold?: boolean
  capped?: boolean
  onChangeNum: (v: number) => void
  onCommit: () => void
  onCancel: () => void
  extraButton?: React.ReactNode
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const cls =
    "budget-inline-input" +
    (!editing ? " budget-inline-input--readonly" : "") +
    (error && editing ? " budget-inline-input--error" : "") +
    (isAuto && !editing ? " budget-inline-input--auto" : "") +
    (capped ? " budget-inline-input--capped" : "")

  const display = draft !== null ? draft : formattedDisplay

  return (
    <div className={cls} title={isAuto ? "Auto-split among siblings — click to override" : undefined}>
      {prefix && <span className="budget-inline-input__prefix">{prefix}</span>}
      <input
        type="text"
        inputMode="decimal"
        className="budget-inline-input__field"
        style={bold ? { fontWeight: 700 } : undefined}
        readOnly={!editing}
        tabIndex={editing ? 0 : -1}
        value={display}
        onChange={(e) => {
          const next = numericOnly(e.target.value)
          setDraft(next)
          const v = parseFloat(next.replace(/,/g, ""))
          if (!isNaN(v)) onChangeNum(v)
        }}
        onFocus={() => editing && setDraft(value.toFixed(2))}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setDraft(null)
            onCancel()
            ;(e.target as HTMLInputElement).blur()
          } else if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur()
          }
        }}
        onBlur={() => {
          setDraft(null)
          onCommit()
        }}
      />
      {suffix && <span className="budget-inline-input__prefix">{suffix}</span>}
      {extraButton}
    </div>
  )
}

function ReadonlyShell({
  prefix,
  suffix,
  valueText,
  bold = false,
  over = false,
  muted = false,
}: {
  prefix?: string
  suffix?: string
  valueText: string
  bold?: boolean
  over?: boolean
  muted?: boolean
}) {
  return (
    <div className="budget-inline-input budget-inline-input--readonly">
      {prefix && <span className="budget-inline-input__prefix">{prefix}</span>}
      <input
        type="text"
        readOnly
        tabIndex={-1}
        className="budget-inline-input__field"
        style={{
          fontWeight: bold ? 600 : undefined,
          color: over ? "#ef4444" : muted ? "#94a3b8" : undefined,
        }}
        value={valueText}
      />
      {suffix && <span className="budget-inline-input__prefix">{suffix}</span>}
    </div>
  )
}
